import { storageManager } from "@/services/storage/storage/storage";
import { joinUrl } from "@/utils/url";

const STORAGE_KEY = "radar-system-config-suffix-override";

export const radarPortOverrideStorage = {
  async get(): Promise<string | null> {
    try {
      await storageManager.initialize();
      const value = await storageManager.getAppState<string>(STORAGE_KEY);
      return value?.trim() || null;
    } catch (error) {
      console.error("Failed to retrieve radar system-config override:", error);
      return null;
    }
  },

  async set(suffix: string): Promise<void> {
    await storageManager.initialize();
    await storageManager.setAppState(STORAGE_KEY, suffix.trim());
  },

  async clear(): Promise<void> {
    await storageManager.initialize();
    await storageManager.deleteAppState(STORAGE_KEY);
  },
};

/**
 * Resolves the system-config URL for a radar. A locally stored developer
 * override (set via the hidden debug tab, or the `radarSystemConfigSuffix`
 * boot query param) replaces everything after the radar's origin when it
 * starts with a port (e.g. ":9999/api/system_config", for testing a custom
 * port on a bare-host radar URL). A path-only override (e.g.
 * "/api/system_config") is instead appended after the radar's existing path,
 * so it doesn't clobber a meaningful path segment like "/radars/{id}".
 * Without an override, falls back to the built-in :8888 default (unless the
 * URL already has an explicit port, an existing path, or is ngrok/localhost).
 */
export function buildSystemConfigUrl(
  radarHttpBaseUrl: string,
  suffixOverride: string | null
): string {
  try {
    const parsedUrl = new URL(radarHttpBaseUrl);
    const hasPath = parsedUrl.pathname !== "/" && parsedUrl.pathname !== "";

    if (suffixOverride) {
      if (suffixOverride.startsWith(":")) {
        const originWithoutPort = parsedUrl.port
          ? parsedUrl.origin.slice(0, -(parsedUrl.port.length + 1))
          : parsedUrl.origin;
        return `${originWithoutPort}${suffixOverride}`;
      }
      return joinUrl(radarHttpBaseUrl, suffixOverride);
    }

    const hasPort = parsedUrl.port !== "";
    const isLocalTesting =
      radarHttpBaseUrl.includes("ngrok") ||
      radarHttpBaseUrl.includes("localhost");

    if (hasPort || hasPath || isLocalTesting) {
      return joinUrl(radarHttpBaseUrl, "api/system_config");
    }

    return joinUrl(`${parsedUrl.origin}:8888`, "api/system_config");
  } catch {
    return joinUrl(radarHttpBaseUrl, "api/system_config");
  }
}
